import axios from "axios";
import { axiosClient } from "../client";

const BASE_URL = process.env.REACT_APP_API_PRESTO_LICENSE;

if (!BASE_URL) {
  console.warn("⚠️ REACT_APP_API_PRESTO_LICENSE is not defined!");
}

// ----------------------
// API Paths
// ----------------------
const API_LICENSE_DETAILS = (locationId) =>
  `${BASE_URL}/location/${locationId}/licenses`;

const API_SINGLE_LICENSE_DETAILS = (locationId, licenseCode) =>
  `${BASE_URL}/internal/location/${locationId}/licenses/details/${encodeURIComponent(
    licenseCode
  )}`;

const API_UPDATE_LICENSE = (locationId) =>
  `${BASE_URL}/location/${locationId}/updateLicense`;

const API_CHANGE_LICENSE_STATUS = (locationId) =>
  `${BASE_URL}/location/${locationId}/changeLicenseStatus`;

const API_CANCEL_CHANGE_REQUEST = (locationId) =>
  `${BASE_URL}/location/${locationId}/cancelChangeRequest`;

const API_APPROVE_CHANGE_REQUEST = (locationId) =>
  `${BASE_URL}/location/${locationId}/approveLicenseRequest`;

const API_CREATE_LICENSE = (locationId) =>
  `${BASE_URL}/location/${locationId}/addlicense`;

const API_LICENSE_CHANGE_REQUESTS = () =>
  `${BASE_URL}/location/getLicenseChangeRequests`;


const API_GET_LOCATION_CHARGEBEE_MAP = (locationId) =>
  `${BASE_URL}/location/${locationId}/chargebee-customer`;

const API_SET_LOCATION_CHARGEBEE_MAP = (locationId) =>
  `${BASE_URL}/location/${locationId}/chargebee-customer`;

const API_GET_ALL_CHARGEBEE_CUSTOMERS = () =>
  `${BASE_URL}/chargebee/customers`;

const API_GET_SINGLE_CHARGEBEE_CUSTOMER = (customerId) =>
  `${BASE_URL}/chargebee/customers/${customerId}`;

const API_GET_CHARGEBEE_SUBSCRIPTIONS_BY_CUSTOMER = (customerId) =>
  `${BASE_URL}/chargebee/customers/${encodeURIComponent(customerId)}/subscriptions`;

// ----------------------
// GET license details
// ----------------------
export const fetchLicenseDetails = (locationId, token) =>
  axiosClient({
    method: "GET",
    url: API_LICENSE_DETAILS(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

// ----------------------
// GET single license details
// ----------------------
export const fetchSingleLicenseDetails = (locationId, licenseCode) =>
  axios({
    method: "GET",
    url: API_SINGLE_LICENSE_DETAILS(locationId, licenseCode),
  });

// ----------------------
// PUT update license
// ----------------------
export const updateLicense = (locationId, data, token) =>
  axiosClient({
    method: "PUT",
    url: API_UPDATE_LICENSE(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data,
  });

// ----------------------
// POST change license status
// ----------------------
export const changeLicenseStatus = (locationId, data, token) =>
  axiosClient({
    method: "POST",
    url: API_CHANGE_LICENSE_STATUS(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data,
  });

// ----------------------
// POST cancel change request
// ----------------------
export const cancelChangeRequest = (locationId, data, token) =>
  axiosClient({
    method: "POST",
    url: API_CANCEL_CHANGE_REQUEST(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data,
  });

// ----------------------
// POST approve change request
// ----------------------
export const approveChangeRequest = (locationId, data, token) =>
  axiosClient({
    method: "POST",
    url: API_APPROVE_CHANGE_REQUEST(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data,
  });

// ----------------------
// POST create new license
// ----------------------
export const createLicense = (locationId, data, token) => {
  if (!token) {
    console.warn("⚠️ No Firebase token provided to createLicense");
  }

  return axiosClient({
    method: "POST",
    url: API_CREATE_LICENSE(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data,
  });
};

// ----------------------
// POST remove license
// ----------------------
export const removeLicense = (locationId, licenseCode, removeReason, token) => {
  if (!token) {
    console.warn("⚠️ No Firebase token provided to removeLicense");
  }

  return axiosClient({
    method: "POST",
    url: `${BASE_URL}/location/${locationId}/license/${encodeURIComponent(
      licenseCode
    )}/remove`,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: {
      removeReason,
    },
  });
};

// ----------------------
// GET all license change requests
// ----------------------
export const getLicenseChangeRequests = (token) =>
  axiosClient({
    method: "GET",
    url: API_LICENSE_CHANGE_REQUESTS(),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

// GET all locations with businessDisplayName
export const getAllLocations = (token) =>
  axiosClient({
    method: "GET",
    url: "https://dev.api.location.prestoexpress.co.uk/?page=1&size=20000",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });


  // ----------------------
// GET location Chargebee map
// ----------------------
export const getLocationChargebeeMap = (locationId, token) =>
  axiosClient({
    method: "GET",
    url: API_GET_LOCATION_CHARGEBEE_MAP(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

// ----------------------
// POST set location Chargebee customer
// ----------------------
export const setLocationChargebeeMap = (locationId, data, token) =>
  axiosClient({
    method: "POST",
    url: API_SET_LOCATION_CHARGEBEE_MAP(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data,
  });

// ----------------------
// DELETE location Chargebee customer
// ----------------------
export const deleteLocationChargebeeMap = (locationId, token) =>
  axiosClient({
    method: "DELETE",
    url: API_SET_LOCATION_CHARGEBEE_MAP(locationId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

// ----------------------
// GET all Chargebee customers
// ----------------------
export const getAllChargebeeCustomers = (token) =>
  axiosClient({
    method: "GET",
    url: API_GET_ALL_CHARGEBEE_CUSTOMERS(),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

// ----------------------
// GET single Chargebee customer
// ----------------------
export const getSingleChargebeeCustomer = (customerId, token) =>
  axiosClient({
    method: "GET",
    url: API_GET_SINGLE_CHARGEBEE_CUSTOMER(customerId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  export const getChargebeeSubscriptionsByCustomer = (customerId, token) =>
  axiosClient({
    method: "GET",
    url: API_GET_CHARGEBEE_SUBSCRIPTIONS_BY_CUSTOMER(customerId),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  export const removeLicenseSubscription = (locationId, licenseCode, token) =>
  updateLicense(
    locationId,
    {
      licenseCode,
      removeSubscriptionId: true,
    },
    token
  );
